import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthProps {
  onLogin: () => void;
}

export function Auth({ onLogin }: AuthProps) {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [verificationUrl, setVerificationUrl] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');
    setVerificationUrl('');
    setShowResendButton(false);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/signup';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, rememberMe }),
      });

      const result = await response.json();

      if (result.success) {
        if (result.requiresVerification) {
          // Signup successful, but requires email verification
          setSuccessMessage(result.message);
          setVerificationUrl(result.verificationUrl || '');
        } else {
          // Login successful
          onLogin();
          navigate('/admin');
        }
      } else {
        setError(result.error || 'Authentication failed');
        if (result.requiresVerification) {
          setShowResendButton(true);
        }
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('Verification email sent! Please check your email.');
        setVerificationUrl(result.verificationUrl || '');
      } else {
        setError(result.error || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('Password reset email sent! Please check your email and follow the instructions to reset your password.');
      } else {
        setError(result.error || 'Failed to send password reset email');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isForgotPassword ? 'Reset your password' : isLogin ? 'Sign in to your account' : 'Create your account'}
          </h2>
          {isForgotPassword && (
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          )}
        </div>
        <form className="mt-8 space-y-6" onSubmit={isForgotPassword ? handleForgotPassword : handleSubmit}>
          <div>
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
              placeholder="Email address"
            />
          </div>
          {!isForgotPassword && (
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                placeholder="Password"
              />
            </div>
          )}

          {isLogin && !isForgotPassword && (
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me for 7 days
              </label>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          {successMessage && (
            <div className="text-green-600 text-sm text-center">
              <p>{successMessage}</p>
              {verificationUrl && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600">For development, click below to verify:</p>
                  <a 
                    href={verificationUrl} 
                    className="text-indigo-600 hover:text-indigo-500 text-xs underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Verify Email Now
                  </a>
                </div>
              )}
            </div>
          )}

          {showResendButton && (
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendVerification}
                className="text-indigo-600 hover:text-indigo-500 text-sm underline"
                disabled={loading}
              >
                Resend verification email
              </button>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 cursor-pointer"
            >
              {loading ? 'Please wait...' : isForgotPassword ? 'Send reset email' : isLogin ? 'Sign in' : 'Sign up'}
            </button>
          </div>

          <div className="space-y-2">
            {!isForgotPassword && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setSuccessMessage('');
                    setVerificationUrl('');
                    setShowResendButton(false);
                  }}
                  className="text-indigo-600 hover:text-indigo-500 text-sm block cursor-pointer mx-auto"
                >
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                </button>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setError('');
                      setSuccessMessage('');
                      setVerificationUrl('');
                      setShowResendButton(false);
                    }}
                    className="text-indigo-600 hover:text-indigo-500 text-sm block cursor-pointer mx-auto"
                  >
                    Forgot your password?
                  </button>
                )}
              </>
            )}
            {isForgotPassword && (
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setIsLogin(true);
                  setError('');
                  setSuccessMessage('');
                  setVerificationUrl('');
                  setShowResendButton(false);
                }}
                className="text-indigo-600 hover:text-indigo-500 text-sm block cursor-pointer mx-auto"
              >
                Back to sign in
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}